import client, { API } from "@/apiClient";
import { getStoredToken } from "@/lib/accessApi";

/** Fetch the global app settings document (singleton). */
export const getSettings = () =>
  client.get("/settings").then((r) => r.data);

/** Update the default ship name. */
export const updateSettings = ({ default_ship_name }) =>
  client
    .put("/settings", { default_ship_name })
    .then((r) => r.data);

/**
 * Phase 8 — PDF layout settings.
 *
 * The backend stores a singleton document with two parallel structures:
 *   - section_colors: { [canonical_key]: "#RRGGBB" }
 *   - section_order:  [ canonical_key, … ]   (one entry per canonical key)
 *
 * The 7 canonical keys are the only valid section identifiers; the
 * server rejects (422) any payload missing/adding a key. Re-exported
 * here so the Settings page can render its rows without round-tripping.
 */
export const PDF_LAYOUT_SECTION_KEYS = [
  "daily_events",
  "special_events",
  "chefs_special",
  "regular_restaurants",
  "specialty_restaurants",
  "buffets",
  "crew_mess_buffet",
  // Phase 16 (B4 — actual root cause) — canape_functions exists in
  // settings.py CANONICAL_SECTION_KEYS but was missing from THIS list,
  // which is what the Section Colours panel iterates over. The label +
  // default order had it; without this entry the colour swatch + label
  // row never rendered for canape_functions, AND the order row label
  // map missed it via `PDF_LAYOUT_SECTION_LABELS[key]` lookup when the
  // server's healed `section_order` returned the appended canape_functions
  // slot. Net effect for the user: anonymous slot 8 with a brown swatch.
  "canape_functions",
];

export const PDF_LAYOUT_SECTION_LABELS = {
  daily_events:          "Daily Events",
  special_events:        "Special Events",
  chefs_special:         "Chef's Special (per-restaurant pill)",
  regular_restaurants:   "Regular Restaurants",
  specialty_restaurants: "Specialty Restaurants",
  buffets:               "Buffets (OVC, Windjammer, …)",
  crew_mess_buffet:      "Crew Mess Buffet",
  // Phase 16 (B4) — canape_functions exists as a canonical section in
  // settings.py (Phase 9 added it) but the label map was never updated,
  // so the 8th row in `Section order` rendered an empty `<span>` next
  // to a brown swatch. The PDF renderer already places this section
  // right after `special_events`; it's the bucket for any event with
  // `is_canape_function=True`.
  canape_functions:      "Canapé Functions",
};

export const DEFAULT_PDF_LAYOUT = {
  section_colors: {
    daily_events:          "#475569",
    special_events:        "#475569",
    chefs_special:         "#F59E0B",
    regular_restaurants:   "#475569",
    specialty_restaurants: "#475569",
    buffets:               "#475569",
    crew_mess_buffet:      "#475569",
    // Phase 16 (B4) — match backend `DEFAULT_SECTION_COLORS`.
    canape_functions:      "#B45309",
  },
  section_order: [
    "regular_restaurants",
    "specialty_restaurants",
    "buffets",
    "crew_mess_buffet",
    "daily_events",
    "special_events",
    // Phase 16 (B4) — canapé functions sit right after special events,
    // mirroring backend `DEFAULT_SECTION_ORDER` in `settings.py`.
    "canape_functions",
    "chefs_special",
  ],
  // Phase 8 add-on — pastry accent (separate top-level field, applies
  // to the PASTRY column tint in every buffet / crew-mess meal block).
  pastry_accent: "#FFF7CC",
};

export const getPdfLayout = () =>
  client.get("/settings/pdf-layout").then((r) => r.data);

export const updatePdfLayout = ({ section_colors, section_order, pastry_accent }) =>
  client
    .put("/settings/pdf-layout", { section_colors, section_order, pastry_accent })
    .then((r) => r.data);

/**
 * Build the PDF export URL for a voyage.
 *
 * Options:
 *   - day:    single-day mode (`?days=<n>`). Only meaningful for per_day layout.
 *   - layout: "per_day" (default) | "single_page". When "single_page",
 *             ALL days are rendered as columns on ONE A2 landscape page.
 *
 * Phase 7+/C — the URL also carries `?token=<bearer>` so a direct `<a>`
 * navigation passes the access gate (the gate accepts both the
 * Authorization header and the query-string fallback). Without this the
 * browser's anchor click sends NO auth header and the download silently
 * 401s — which was a latent prod-side bug pre-Turn-2.
 */
function _attachToken(url) {
  const tok = getStoredToken();
  if (!tok) return url;
  return `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(tok)}`;
}

export const buildVoyagePdfUrl = (voyageId, { day, layout, sections, venues, buffet_layout } = {}) => {
  const base = `${API}/voyages/${voyageId}/export/pdf`;
  const params = new URLSearchParams();
  if (day != null) params.set("days", String(day));
  if (layout && layout !== "per_day") params.set("layout", layout);
  // Phase 8+1 — filtered exports.
  if (Array.isArray(sections) && sections.length > 0) {
    params.set("sections", sections.join(","));
  } else if (typeof sections === "string" && sections) {
    params.set("sections", sections);
  }
  if (Array.isArray(venues) && venues.length > 0) {
    params.set("venues", venues.join(","));
  } else if (typeof venues === "string" && venues) {
    params.set("venues", venues);
  }
  // Phase 14 — buffet grouping mode for OVC + crew mess sections.
  if (buffet_layout && buffet_layout !== "kitchen") {
    params.set("buffet_layout", buffet_layout);
  }
  const qs = params.toString();
  return _attachToken(qs ? `${base}?${qs}` : base);
};

// Canonical PDF section keys + display labels — mirrored from
// backend/settings.py::CANONICAL_SECTION_KEYS. The frontend needs them
// for the Advanced filter dialog.
export const PDF_FILTER_SECTION_KEYS = [
  "regular_restaurants",
  "specialty_restaurants",
  "buffets",
  "crew_mess_buffet",
  "daily_events",
  "special_events",
  // Phase 9 — Canapé Function events.
  "canape_functions",
  "chefs_special",
];

export const PDF_FILTER_SECTION_LABELS = {
  regular_restaurants:   "Regular Restaurants",
  specialty_restaurants: "Specialty Restaurants",
  buffets:               "Buffets",
  crew_mess_buffet:      "Crew Mess Buffet",
  daily_events:          "Daily Events",
  special_events:        "Special Events",
  canape_functions:      "Canapé Functions",
  chefs_special:         "Chef's Special",
};

// Restaurant `type` → section_key that hosts that type's venues.
export const PDF_TYPE_TO_SECTION = {
  regular:   "regular_restaurants",
  specialty: "specialty_restaurants",
  buffet:    "buffets",
};

/**
 * Phase 7+/C — full-voyage Excel (.xlsx) workbook download URL.
 * Same auth-via-?token= pattern as the PDF helper above. Returns
 * one multi-sheet workbook: 1 "Voyage Overview" sheet + N day sheets.
 *
 * Phase 14 — supports `buffet_layout=kitchen|line` to regroup OVC +
 * crew mess buffet cells by line / island in each Day sheet.
 */
export const buildVoyageXlsxUrl = (voyageId, { buffet_layout } = {}) => {
  const base = `${API}/voyages/${voyageId}/export/xlsx`;
  if (buffet_layout && buffet_layout !== "kitchen") {
    return _attachToken(`${base}?buffet_layout=${encodeURIComponent(buffet_layout)}`);
  }
  return _attachToken(base);
};

// ---------------------------------------------------------------------------
// Phase 10 — Backup & Restore helpers
// ---------------------------------------------------------------------------
/** Single-voyage JSON export (mode=voyage). */
export const buildVoyageJsonExportUrl = (voyageId) =>
  _attachToken(`${API}/voyages/${voyageId}/export?mode=voyage`);

/** Single-voyage bundle ZIP (mode=bundle) — voyage + referenced libs + photos. */
export const buildVoyageBundleExportUrl = (voyageId) =>
  _attachToken(`${API}/voyages/${voyageId}/export?mode=bundle`);

/** Full workspace backup ZIP — voyages + ALL libraries + settings + photos. */
export const buildFullBackupUrl = () =>
  _attachToken(`${API}/backup/full`);

/**
 * POST a backup file to /api/backup/import. The `opts` shape:
 *   - peek: true        → just return the manifest, no writes
 *   - mode: "replace"|"merge"   (required for full backups)
 *   - voyage_conflict: "replace"|"new"  (single-voyage uploads)
 *
 * The backend auto-detects the file kind; voyage-conflict 409s are
 * surfaced as `{conflict, instructions}` via a non-200 response so the
 * caller can handle them in-band.
 */
export const importBackup = (file, { peek, mode, voyage_conflict } = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  const params = new URLSearchParams();
  if (peek) params.set("peek", "true");
  if (mode) params.set("mode", mode);
  if (voyage_conflict) params.set("voyage_conflict", voyage_conflict);
  const qs = params.toString();
  return client
    .post(`/backup/import${qs ? `?${qs}` : ""}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      // Treat 409 as a "soft" error the caller handles — bubble it up
      // rather than letting axios reject.
      validateStatus: (s) => (s >= 200 && s < 300) || s === 409,
    })
    .then((r) => ({ status: r.status, data: r.data }));
};

/**
 * Wipe every voyage + library + clear `default_ship_name` and persist a
 * `user_initiated_reset` marker so the startup autoseeder no longer
 * re-populates libraries on the next backend restart.
 *
 * Requires an explicit `confirm=YES_RESET_EVERYTHING` query string —
 * encoded here so callers can't accidentally fire this from the wrong place.
 */
export const resetAllData = () =>
  client
    .post("/admin/reset-all-data", null, {
      params: { confirm: "YES_RESET_EVERYTHING" },
    })
    .then((r) => r.data);

// ---------------------------------------------------------------------------
// Phase 11 — Print multi-library station PDF handout
// ---------------------------------------------------------------------------
/**
 * POST a selection of station-style library ids and receive a printable
 * A4 PDF. `selections` keys must be a subset of:
 *   - "lib_live_stations"
 *   - "lib_crew_mess_live_stations"
 *   - "lib_standard_stations"
 *   - "lib_theme_buffets"
 * Returns a Promise resolving to ``{blob, filename}`` so the caller can
 * trigger an anchor-click download with the server-suggested filename.
 */
export const printStationsPdf = ({ selections, title, paper } = {}) =>
  client
    .post(
      "/library/print-stations",
      // Hotfix 2 — `paper` is optional; backend treats null / undefined
      // as the legacy A4 default. Only emit "letter" when explicitly
      // chosen so existing callers keep their behaviour.
      { selections, title: title || null, paper: paper || null },
      { responseType: "blob" },
    )
    .then((r) => {
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      return {
        blob: r.data,
        filename: m ? m[1] : "voyagemenu_stations.pdf",
      };
    });
  client
    .post("/admin/reset-all-data", null, {
      params: { confirm: "YES_RESET_EVERYTHING" },
    })
    .then((r) => r.data);
